import {
  Box,
  ListItem,
  Text,
  UnorderedList,
  useColorMode,
} from "@chakra-ui/react";
import { GetProgrammaticCampaignAccountAndPersonaInsightsRO } from "@intentsify/dto";
import { decimalToPercentage } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { Card } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { MdAnalytics } from "react-icons/md";
import { DisplayPerformanceFilters } from "../../../../types";

const MIN_NUMBER_OF_ACCOUNTS_FOR_INSIGHTS = 50;

type AccountAndPersonaInsightsProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getAccountAndPersonasInsights: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignAccountAndPersonaInsightsRO>;
};

const TITLE = "Account & Persona Insights";

const AccountAndPersonaInsights = (props: AccountAndPersonaInsightsProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <AccountAndPersonaInsightsContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const AccountAndPersonaInsightsContent = ({
  campaignId,
  filters,
  getAccountAndPersonasInsights,
}: AccountAndPersonaInsightsProps) => {
  const { colorMode } = useColorMode();

  const {
    data: accountAndPersonaInsightsData,
    isFetching: isLoadingAccountAndPersonasInsightsData,
  } = useQuery({
    queryKey: ["accountAndPersonasInsights", campaignId, filters],
    queryFn: async () =>
      await getAccountAndPersonasInsights(campaignId, filters),
  });

  const insightsColor = colorMode === "dark" ? "gray.25" : "gray.600";

  const numberOfUnengagedAccounts =
    accountAndPersonaInsightsData?.accountAndPersonaInsights
      ?.numberOfUnengagedAccounts ?? 0;

  return (
    <Card
      isLoading={isLoadingAccountAndPersonasInsightsData}
      title={TITLE}
      noDataMessage='This visualization is designated for programs that have selected target personas. To configure your target personas, click on the "Target Personas" tab in the navigation bar.'
      noDataIcon={MdAnalytics}
      hasData={!!accountAndPersonaInsightsData?.accountAndPersonaInsights}
    >
      <Box fontSize="sm" padding={2} color={insightsColor}>
        <UnorderedList spacing={3}>
          {accountAndPersonaInsightsData?.accountAndPersonaInsights
            ?.mostEngagedPersona && (
            <ListItem>
              <Text>
                The{" "}
                {
                  accountAndPersonaInsightsData?.accountAndPersonaInsights
                    .mostEngagedPersona
                }{" "}
                Persona is more likely to engage with your digital ads.
              </Text>
            </ListItem>
          )}
          {numberOfUnengagedAccounts >= MIN_NUMBER_OF_ACCOUNTS_FOR_INSIGHTS && (
            <ListItem>
              <Text>
                There are {numberOfUnengagedAccounts} accounts that have yet to
                engage with your creative ads - we recommend optimizing your
                campaign by reallocating spend from top domains that are not
                capturing engagements to top performing accounts as well as
                moving budget into mobile devices.
              </Text>
            </ListItem>
          )}
          <ListItem>
            <Text>
              We've reached{" "}
              {decimalToPercentage(
                accountAndPersonaInsightsData?.accountAndPersonaInsights
                  ?.accountsReached || 0
              )}
              % of your Target Account List,{" "}
              {decimalToPercentage(
                accountAndPersonaInsightsData?.accountAndPersonaInsights
                  ?.accountsEngaged || 0
              )}
              % of those accounts engaged with your digital ads.
            </Text>
          </ListItem>
        </UnorderedList>
      </Box>
    </Card>
  );
};

export { AccountAndPersonaInsights };
